import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Image, Modal, useWindowDimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth, hasEmergencyContact } from '../context/AuthContext';
import { attendanceAPI, homeworkAPI, notificationAPI, studentAPI, feeAPI, examAPI, eventsAPI } from '../services/api';
import { uploadProfilePhoto } from '../utils/profilePhotoUpload';
import { mapHomework } from '../utils/mappers';
import { WebPageShell } from '../components/common/WebPageShell';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { FeeOnboardingModal } from '../components/fee/FeeOnboardingModal';
import { ProfileOnboardingModal } from '../components/profile/ProfileOnboardingModal';
import { ContactAdminModal } from '../components/common/ContactAdminModal';
import { PendingAmountBanner } from '../components/wallet/PendingAmountBanner';
import { HomeWalletSection } from '../components/wallet/HomeWalletSection';
import { useDelayedFlag } from '../hooks/useDelayedFlag';
import { useOngoingClass } from '../hooks/useOngoingClass';
import { useTodayAttendanceStatus } from '../hooks/useTodayAttendanceStatus';
import { isAwayToday } from '../utils/todayAttendance';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import Ionicon from 'react-native-vector-icons/Ionicons';
import { HomeworkItem } from '../types';
import { useNotifications } from '../context/NotificationContext';
import { toActivity } from '../utils/notificationMapper';
import type { ExamFocus } from '../utils/examDetail';
import { formatRelativeTime, formatLocalDateOnly } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { Toast } from '../components/common/Toast';
import { CameraCaptureModal } from '../components/common/CameraCaptureModal.web';
import { useCxcOptions } from '../hooks/useCxcOptions';
import { CxcBanner } from '../components/cxc/CxcBanner';
import { cxcSittings, isCxcRegistrationOpen, isStudentEligibleForCxc } from '../utils/cxcRegistration';
import { useCountUp } from '../components/motion/Motion';
import { portalPages } from '../utils/portalSearch';

/** First + last initials, e.g. "Aarav Sharma" -> "AS", matching the web portal's tile. */
const getInitials = (name?: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// A plain string handed to JSX renders as that DOM tag. This project has no "dom" lib
// (see the (globalThis as any) casts throughout ProfileScreen.web.tsx), so a literal
// <div> tag has no JSX.IntrinsicElements entry to type-check against — assigning it to
// a capitalised binding is what lets JSX accept it, same trick CameraCaptureModal uses
// for <video>. Needed here because RN's View has no onDragOver/onDrop props.
const Div: any = 'div';

type NavigationProp = NativeStackNavigationProp<any>;

const CircularProgress = ({
  size = 120,
  strokeWidth = 10,
  percentage = 0,
  color = '#3B82F6',
  emptyColor = '#EFF6FF',
  children,
}: any) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const radius = size / 2;
  // The ring fills up to its value as the dashboard opens.
  const p = Math.min(Math.max(useCountUp(percentage), 0), 100);
  const firstHalfRotation = Math.min(p, 50) * 3.6;
  const secondHalfRotation = Math.max(p - 50, 0) * 3.6;

  return (
    <View style={[styles.cpContainer, { width: size, height: size }]}>
      <View style={[StyleSheet.absoluteFill, styles.cpBackground, { borderRadius: radius, borderWidth: strokeWidth, borderColor: emptyColor }]} />
      <View style={[styles.cpHalfContainer, { width: radius, height: size, left: radius }]}>
        <View style={[
          styles.cpHalfCircle,
          {
            width: size, height: size, borderRadius: radius,
            borderWidth: strokeWidth,
            borderTopColor: color, borderRightColor: color,
            left: -radius,
            transform: [{ rotate: `${-135 + firstHalfRotation}deg` }]
          }
        ]} />
      </View>
      {p > 50 && (
        <View style={[styles.cpHalfContainer, { width: radius, height: size, left: 0 }]}>
          <View style={[
            styles.cpHalfCircle,
            {
              width: size, height: size, borderRadius: radius,
              borderWidth: strokeWidth,
              borderTopColor: color, borderRightColor: color,
              left: 0,
              transform: [{ rotate: `${45 + secondHalfRotation}deg` }]
            }
          ]} />
        </View>
      )}
      {children}
    </View>
  );
};

/** A percentage counting up alongside its ring; once there, shown exactly as `format` says. */
const CountUpPercent = ({ value, style, format = (v: number) => v.toFixed(0) }: {
  value: number;
  style: any;
  format?: (value: number) => string;
}) => {
  const shown = useCountUp(value);
  return <Text style={style}>{shown === value ? format(value) : Math.round(shown)}%</Text>;
};

interface QuickAction {
  id: string;
  title: string;
  subtitle: string;
  /** alert: something needs doing (fees due); good: all clear. */
  tone?: 'alert' | 'good';
  /** A Feather icon name, and the card's two colours. */
  icon: string;
  tint: string;
  color: string;
  onPress: () => void;
}

/** A page's icon and colours, as the header search shows it. */
const pageLook = (route: string) => {
  const page = portalPages().find(p => p.route === route);
  return { icon: page?.icon ?? 'circle', tint: page?.tint ?? '#F1F5F9', color: page?.color ?? '#475569' };
};

/**
 * One quick-action card (owner, 2 Nov 2026: "make these cards standard"): the same size and
 * layout for every action - icon and arrow on top, title and up to two lines below - with a
 * soft glow of the action's colour in the corner. `row` is the compact form for small phones.
 */
const QuickActionCard = ({ title, subtitle, tone, icon, tint, color, onPress, layout, basis }: QuickAction & {
  layout: 'tile' | 'row';
  basis: string;
}) => {
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createQuickStyles(colors), [colors]);
  // react-native-web hands backgroundImage to the browser (@shared/commerce/Gradient.web.tsx).
  const glow = { backgroundImage: `radial-gradient(170px 170px at 100% 0%, ${color}${isDark ? '33' : '1A'} 0%, transparent 70%)` } as any;
  const subtitleColor = tone === 'alert' ? '#EA580C' : tone === 'good' ? '#16A34A' : colors.textSecondary;
  const iconBox = (
    <View style={[styles.icon, { backgroundColor: isDark ? `${color}33` : tint }]}>
      <FeatherIcon name={icon} size={20} color={color} />
    </View>
  );

  if (layout === 'row') {
    return (
      <TouchableOpacity style={[styles.card, styles.row, glow]} onPress={onPress} activeOpacity={0.85} accessibilityRole="button">
        {iconBox}
        <View style={styles.rowText}>
          <Text style={styles.title} numberOfLines={1}>{title}</Text>
          <Text style={[styles.subtitle, { color: subtitleColor }]} numberOfLines={1}>{subtitle}</Text>
        </View>
        <FeatherIcon name="chevron-right" size={18} color={colors.textMuted} />
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity style={[styles.card, styles.tile, { flexBasis: basis }, glow]} onPress={onPress} activeOpacity={0.85} accessibilityRole="button">
      <View style={styles.tileTop}>
        {iconBox}
        <View style={styles.arrow}>
          <FeatherIcon name="arrow-up-right" size={15} color={colors.textSecondary} />
        </View>
      </View>
      <View>
        <Text style={styles.title} numberOfLines={1}>{title}</Text>
        <Text style={[styles.subtitle, { color: subtitleColor }]} numberOfLines={2}>{subtitle}</Text>
      </View>
    </TouchableOpacity>
  );
};

const createQuickStyles = (colors: ThemeColors) => StyleSheet.create({
  card: {
    backgroundColor: colors.card,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#032158',
    shadowOpacity: 0.06,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
    overflow: 'hidden',
  },
  tile: {
    flexGrow: 1,
    minHeight: 150,
    padding: 18,
    justifyContent: 'space-between',
  },
  tileTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrow: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 18,
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 76,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    marginHorizontal: 12,
  },
});

interface AlertItem {
  id: string;
  color: string;
  title: string;
  subtitle: string;
  date: Date;
  /** An exam alert: the paper it opens on the exam details card. */
  examFocus?: ExamFocus;
}

interface DashboardData {
  attendance: any | null;
  pendingHomework: any[];
  unreadNotifications: number;
  performanceSummary?: any | null;
  totalFeeDue?: number;
  nextFeeDue?: { installmentName: string; dueDate: string } | null;
  upcomingExamDate?: string | null;
  alerts: AlertItem[];
  examSchedule?: any[];
}

const formatCountdown = (date: Date) => {
  const diffDays = Math.round((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1d';
  return `${diffDays}d`;
};

export function HomeScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isSmallMobile = width < 480;
  const { selectedStudent, logout, refreshSelectedStudent } = useAuth();
  const { notifications } = useNotifications();
  // The real period in progress from the timetable — null when nothing is live.
  const timetableClass = useOngoingClass(selectedStudent?.classId);
  // A child on leave (or absent) today is not in that lesson, whatever the timetable says.
  const todayStatus = useTodayAttendanceStatus(selectedStudent?.id, selectedStudent?.classId);
  const ongoingClass = isAwayToday(todayStatus) ? null : timetableClass;
  // True while at least one sitting is open, displaying both official sitting deadlines.
  // Open/closed as the server reports it (Cayman time); hidden until it has answered.
  const { options: cxcOptions } = useCxcOptions();
  const sittingsWithStatus = useMemo(() => cxcSittings(cxcOptions), [cxcOptions]);
  const isCxcOpen = isCxcRegistrationOpen(sittingsWithStatus);

  const recentAlerts = notifications
    .slice(0, 4)
    .map(msg => toActivity(msg, false));

  const needsFee = selectedStudent?.isFeeStructureSelected === false;
  const needsProfile = !needsFee && !hasEmergencyContact(selectedStudent);
  const feeModalReady = useDelayedFlag(needsFee, 500);
  const profileModalReady = useDelayedFlag(needsProfile, 500);
  const [data, setData] = useState<DashboardData>({
    attendance: null,
    pendingHomework: [],
    unreadNotifications: 0,
    performanceSummary: null,
    totalFeeDue: 0,
    nextFeeDue: null,
    upcomingExamDate: null,
    alerts: [],
    examSchedule: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [contactAdminVisible, setContactAdminVisible] = useState(false);

  const { displayPhoto, refetch: refetchPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Rendered in a top-level Modal (its own layer, above everything) rather than a
  // nested absolute View under the card — a later sibling (the details grid) could
  // otherwise paint over it, since neither had an explicit stacking context relative
  // to the other.
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [photoToast, setPhotoToast] = useState<{ visible: boolean; message: string; type?: 'success' | 'error' }>({
    visible: false,
    message: '',
  });

  const uploadPhotoFile = useCallback(async (file: File) => {
    if (!selectedStudent?.id) return;

    setUploadingPhoto(true);

    const result = await uploadProfilePhoto(selectedStudent.id, {
      name: file.name,
      size: file.size,
      value: file,
    });
    setUploadingPhoto(false);

    if (result.ok) {
      refetchPhoto();
      setPhotoToast({ visible: true, message: 'Photo updated successfully.', type: 'success' });
    } else {
      setPhotoToast({ visible: true, message: result.message, type: 'error' });
    }
  }, [selectedStudent, refetchPhoto]);

  const [showCameraModal, setShowCameraModal] = useState(false);

  const handleUploadFromLibrary = useCallback(() => {
    setShowPhotoMenu(false);
    const docObj = (globalThis as any).document;
    if (!docObj?.createElement) return;

    const input = docObj.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file: File | undefined = e.target.files?.[0];
      if (file) uploadPhotoFile(file);
    };
    input.click();
  }, [uploadPhotoFile]);

  // A real camera preview (CameraCaptureModal, via getUserMedia) rather than an
  // <input capture>, which does nothing on desktop browsers and behaves
  // inconsistently across mobile ones.
  const handleCapturePhoto = useCallback(() => {
    setShowPhotoMenu(false);
    // React Native Web's Modal mounts/unmounts a portal node per instance. Opening
    // the camera modal in the SAME tick that closes this one raced the two portals
    // on web — the upload modal's close sometimes won, and the camera modal never
    // became visible even though showCameraModal was true. Letting this one fully
    // close first (one frame is enough) makes the camera modal open reliably.
    requestAnimationFrame(() => setShowCameraModal(true));
  }, []);

  const handleDragOver = useCallback((e: any) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: any) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((e: any) => {
    e.preventDefault();
    setIsDragActive(false);
    const file: File | undefined = e.dataTransfer?.files?.[0];
    if (!file) return;
    setShowPhotoMenu(false);
    uploadPhotoFile(file);
  }, [uploadPhotoFile]);

  const hasPhoto = !!displayPhoto;
  const studentName = selectedStudent?.name || '';

  const loadDashboard = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      return;
    }
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      const end = new Date(now);
      end.setDate(now.getDate() + 30);

      const formatDate = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const todayDateInt = parseInt(`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`, 10);
      const academicYear = selectedStudent.academicYear || now.getFullYear();

      const [attendanceRes, homeworkRes, notifRes, performanceRes, invoiceRes, scheduleRes, eventsRes] = await Promise.allSettled([
        attendanceAPI.getAttendance(selectedStudent.id, selectedStudent.classId, now.getFullYear(), now.getMonth() + 1),
        homeworkAPI.getHomeworkByClass(selectedStudent.classId, formatDate(start), formatDate(end)),
        notificationAPI.getNotifications(1, 1),
        studentAPI.getStudentPerformanceSummary(selectedStudent.id, academicYear, todayDateInt),
        feeAPI.getStudentInvoices(selectedStudent.id),
        examAPI.getParentExamSchedule(selectedStudent.classId, `${academicYear}`),
        eventsAPI.getEvents(),
      ]);

      const att = attendanceRes.status === 'fulfilled' ? attendanceRes.value.data : null;
      const homeworkList: HomeworkItem[] =
        homeworkRes.status === 'fulfilled' ? (homeworkRes.value.data?.data ?? []).map(mapHomework) : [];
      const performanceSummary = performanceRes.status === 'fulfilled' ? performanceRes.value.data : null;
      const today = new Date(new Date().toDateString());

      let totalFeeDue = 0;
      let nextFeeDue: { installmentName: string; dueDate: string } | null = null;
      if (invoiceRes.status === 'fulfilled') {
        const invoiceData = invoiceRes.value.data;
        const invoiceList = Array.isArray(invoiceData) ? invoiceData : invoiceData?.data ?? [];
        const unpaidInvoices: Array<{ installmentName: string; dueDate: string; balance: number }> = [];
        totalFeeDue = invoiceList.reduce((sum: number, f: any, i: number) => {
          const amount = f.GrandTotal ?? f.grandTotal ?? 0;
          const paidAmount = f.PaidAmount ?? f.paidAmount ?? 0;
          const balance = f.BalanceAmount ?? f.balanceAmount ?? (amount - paidAmount);
          const dueDate = f.DueDate ?? f.dueDate ?? f.InvoiceDate ?? f.invoiceDate ?? '';
          if (balance > 0) {
            const type = f.RefType ?? f.refType ?? '';
            unpaidInvoices.push({
              installmentName: f.Description ?? f.description ?? (type || `Installment ${i + 1}`),
              dueDate,
              balance,
            });
          }
          return sum + balance;
        }, 0);
        if (unpaidInvoices.length > 0) {
          unpaidInvoices.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
          nextFeeDue = { installmentName: unpaidInvoices[0].installmentName, dueDate: unpaidInvoices[0].dueDate };
        }
      }

      let upcomingExamDate: string | null = null;
      let examSchedule: any[] = [];
      if (scheduleRes.status === 'fulfilled') {
        const exams = scheduleRes.value.data?.exams || [];
        examSchedule = exams;
        const schedules: string[] = [];
        exams.forEach((exam: any) => {
          exam.classSchedules?.forEach((cls: any) => {
            cls.subjectTimetable?.forEach((subject: any) => {
              if (subject.examDate) schedules.push(subject.examDate);
            });
          });
        });
        const futureExams = schedules.filter(dateStr => new Date(dateStr).getTime() >= Date.now() - 24 * 60 * 60 * 1000);
        if (futureExams.length > 0) {
          futureExams.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
          upcomingExamDate = new Date(futureExams[0]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
      }

      // Recent alerts: fee dues, exams (next 3 days only), and events happening in the next 7 days.
      const alertWindowEnd = new Date(today);
      alertWindowEnd.setDate(today.getDate() + 7);
      const examAlertWindowEnd = new Date(today);
      examAlertWindowEnd.setDate(today.getDate() + 3); // Exams: only next 3 days
      const alerts: AlertItem[] = [];

      if (invoiceRes.status === 'fulfilled') {
        const invoiceData = invoiceRes.value.data;
        const invoiceList = Array.isArray(invoiceData) ? invoiceData : invoiceData?.data ?? [];
        invoiceList.forEach((f: any, idx: number) => {
          const amount = f.GrandTotal ?? f.grandTotal ?? 0;
          const paidAmount = f.PaidAmount ?? f.paidAmount ?? 0;
          const balance = f.BalanceAmount ?? f.balanceAmount ?? (amount - paidAmount);
          const isPaid = f.IsPaid ?? f.isPaid ?? false;
          const dueDateStr = f.DueDate ?? f.dueDate ?? f.InvoiceDate ?? f.invoiceDate;
          if (isPaid || balance <= 0 || !dueDateStr) return;

          const dueDate = new Date(dueDateStr);
          if (dueDate > alertWindowEnd) return;
          const isOverdueNow = dueDate < today;

          alerts.push({
            id: `fee-${f.StudentInvoiceId ?? f.studentInvoiceId ?? idx}`,
            color: isOverdueNow ? '#EF4444' : '#F59E0B',
            title: isOverdueNow ? 'Fee overdue' : 'Fee due reminder',
            subtitle: `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })} due by ${dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
            date: dueDate,
          });
        });
      }

      if (scheduleRes.status === 'fulfilled') {
        const exams = scheduleRes.value.data?.exams || [];
        exams.forEach((exam: any) => {
          exam.classSchedules?.forEach((cls: any) => {
            cls.subjectTimetable?.forEach((subject: any, idx: number) => {
              if (!subject.examDate) return;
              const examDate = new Date(subject.examDate);
              // Only show exams in next 3 days (not 7)
              if (examDate < today || examDate > examAlertWindowEnd) return;

              alerts.push({
                id: `exam-${subject.subjectId ?? idx}-${subject.examDate}`,
                examFocus: { examId: exam.examId ?? exam.ExamId ?? null, subjectId: subject.subjectId ?? null, examDate: String(subject.examDate) },
                color: '#0B5ED7',
                title: `${subject.subjectName || 'Exam'} scheduled`,
                subtitle: `Pre-Tested on ${examDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
                date: examDate,
              });
            });
          });
        });
      }

      if (eventsRes.status === 'fulfilled') {
        const eventsData = eventsRes.value.data;
        const rawEvents = Array.isArray(eventsData) ? eventsData : eventsData?.data ?? [];
        rawEvents.forEach((r: any, idx: number) => {
          const dateStr = r.eventDate || r.EventDate || r.startDate || r.StartDate;
          if (!dateStr) return;
          const eventDate = new Date(dateStr);
          if (eventDate < today || eventDate > alertWindowEnd) return;

          alerts.push({
            id: `event-${r.id ?? r.Id ?? idx}`,
            color: '#10B981',
            title: r.title || r.Title || 'School Event',
            subtitle: `On ${eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
            date: eventDate,
          });
        });
      }

      alerts.sort((a, b) => a.date.getTime() - b.date.getTime());

      setData({
        attendance: att
          ? {
            totalDays: att.totalWorkingDays ?? 0,
            presentDays: att.totalPresentDays ?? 0,
            absentDays: att.totalAbsentDays ?? 0,
            lateDays: att.totalLeaveDays ?? 0,
            holidayDays: 0,
            attendancePercentage: att.attendancePercentage ?? 0,
            month: att.month ?? '',
            year: att.year ?? new Date().getFullYear(),
          }
          : null,
        pendingHomework: homeworkList.filter(hw => hw.status !== 'submitted' && (!hw.dueDate || new Date(hw.dueDate) >= today)),
        unreadNotifications: notifRes.status === 'fulfilled' ? (notifRes.value.data as any)?.unreadCount ?? 0 : 0,
        performanceSummary,
        totalFeeDue,
        nextFeeDue,
        upcomingExamDate,
        alerts: alerts.slice(0, 6),
        examSchedule,
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (isLoading) {
    // The page keeps its header while it loads, with the standard loader inside it.
    return <WebPageShell title="Dashboard" contentStyle={{ maxWidth: 800 }}><LoadingSpinner message="Loading dashboard..." /></WebPageShell>;
  }

  // Prefer the dedicated attendance endpoint (the same one the Attendance screen
  // itself uses) over performanceSummary's own attendance figure: `??` only falls
  // through on null/undefined, so a legitimate 0% from performanceSummary would
  // otherwise silently win over a correct, non-zero value from `data.attendance`.
  const attendancePercentage = data.attendance
    ? data.attendance.attendancePercentage
    : data.performanceSummary?.attendancePercentage ?? 0;
  // No register taken for the child yet this month (e.g. just enrolled): N/A, not 0% (bug 4556).
  const noAttendanceYet = !!data.attendance && data.attendance.totalDays === 0;
  const academicPercentage = data.performanceSummary?.lastExamPercentage ?? 0;

  // Each card takes its icon and colours from the header search's page (utils/portalSearch),
  // so a page looks the same wherever it is offered.
  const feeDue = data.totalFeeDue ?? 0;
  const quickActions: QuickAction[] = [
    {
      ...pageLook('Fees'),
      id: 'Fees',
      title: 'Pay fees',
      subtitle: feeDue > 0
        ? `$${feeDue.toLocaleString('en-US', { minimumFractionDigits: 2 })} due`
          + (data.nextFeeDue?.dueDate ? ` · ${formatLocalDateOnly(data.nextFeeDue.dueDate)}` : '')
        : 'Nothing due right now',
      tone: feeDue > 0 ? 'alert' : 'good',
      onPress: () => navigation.navigate('Fees' as any, { backTo: 'Home' } as any),
    },
    {
      ...pageLook('Attendance'),
      id: 'Attendance',
      title: 'Attendance',
      subtitle: 'Daily and monthly reports',
      onPress: () => navigation.navigate('Attendance', { backTo: 'Home' } as any),
    },
    {
      ...pageLook('Leaves'),
      id: 'Leaves',
      title: 'Leave',
      subtitle: 'Apply and see history',
      onPress: () => navigation.navigate('Leaves', { backTo: 'Home' } as any),
    },
    {
      ...pageLook('ExamResults'),
      id: 'ExamResults',
      title: 'Results',
      subtitle: data.upcomingExamDate ? `Next exam ${data.upcomingExamDate}` : 'Marks and report cards',
      onPress: () => navigation.navigate('ExamResults', { backTo: 'Home' } as any),
    },
    {
      ...pageLook('EarlyPickup'),
      id: 'EarlyPickup',
      title: 'Early pickup',
      subtitle: 'Request and track',
      onPress: () => navigation.navigate('EarlyPickup', { backTo: 'Home' } as any),
    },
    {
      id: 'contact',
      title: 'Contact admin',
      subtitle: 'Message the school office',
      icon: 'mail',
      tint: '#EDE9FE',
      color: '#7C3AED',
      onPress: () => setContactAdminVisible(true),
    },
  ];

  return (
    <>
      <WebPageShell title="Dashboard" contentStyle={{ maxWidth: 800 }}>
        {/* Fees plus wallet bills for every child, with Pay now (owner, 16 Oct 2026). */}
        <PendingAmountBanner onPay={() => navigation.navigate('Wallet' as any, { tab: 'pay' } as any)} />

        {/* The child's wallet, today's order, Order food and Wallet (design screen 1, 19 Oct 2026). */}
        <HomeWalletSection
          onOpenWallet={() => navigation.navigate('Wallet' as any, { backTo: 'Home' } as any)}
          onOrderFood={() => navigation.navigate('Canteen' as any, { backTo: 'Home' } as any)} />
        <View style={[styles.cardHeader, styles.homeSectionHeader]}>
          <Text style={styles.cardTitle}>Student details</Text>
          <TouchableOpacity style={styles.viewProfileBtn} onPress={() => navigation.navigate('Profile' as any)}>
            <Text style={styles.viewProfileText}>View profile</Text>
            <FeatherIcon name="chevron-right" size={14} color={colors.primary} />
          </TouchableOpacity>
        </View>
        <View style={[styles.card, styles.homeSectionCard, { marginBottom: 24 }]}>
          <View style={[styles.studentCardRow, isMobile && styles.studentCardRowMobile]}>
            <View style={styles.studentPhotoWrapper}>
              {hasPhoto ? (
                <Image source={{ uri: displayPhoto }} style={styles.studentPhoto as any} />
              ) : (
                <View style={styles.studentPhotoInitials}>
                  <Text style={styles.studentPhotoInitialsText}>{getInitials(studentName) || '?'}</Text>
                </View>
              )}

              {uploadingPhoto && (
                <View style={styles.studentPhotoUploadOverlay}>
                  <ActivityIndicator size="small" color="#FFFFFF" />
                </View>
              )}

              <TouchableOpacity
                style={styles.studentPhotoEditBadge}
                onPress={() => setShowPhotoMenu(true)}
                disabled={uploadingPhoto}
              >
                <FeatherIcon name="camera" size={14} color={colors.primary} />
              </TouchableOpacity>
            </View>

            <View style={[styles.detailsGrid, isMobile && styles.detailsGridMobile]}>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>STUDENT NAME</Text>
                <Text style={styles.detailValue} numberOfLines={1}>{selectedStudent?.name || '-'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Grade</Text>
                <Text style={styles.detailValue}>{selectedStudent?.class || '-'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>S.ID</Text>
                <Text style={styles.detailValue}>{selectedStudent?.studentIdentificationNumber || '-'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>ACADEMIC YEAR</Text>
                <Text style={styles.detailValue}>
                  {selectedStudent?.academicYear ? `${selectedStudent.academicYear}-${String(selectedStudent.academicYear + 1).slice(-2)}` : '-'}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Performance summary</Text>
          {data.performanceSummary?.classRank && (
            <View style={styles.rankBadge}>
              <Text style={styles.rankText}>Rank #{data.performanceSummary.classRank} / {data.performanceSummary.totalStudents || 40}</Text>
            </View>
          )}
        </View>
        <View style={[styles.card, { marginBottom: 24 }]}>
          <View style={styles.performanceCharts}>
            <View style={[styles.chartContainer, styles.chartContainerBlue]}>
              <CircularProgress percentage={attendancePercentage} color="#3B82F6" emptyColor="#EFF6FF">
                {noAttendanceYet ? (
                  <Text style={[styles.chartValueBlue, { fontSize: 20 }]}>N/A</Text>
                ) : (
                  <CountUpPercent value={attendancePercentage} style={styles.chartValueBlue} />
                )}
                <Text style={styles.chartLabel}>ATTEND.</Text>
              </CircularProgress>
            </View>
            <View style={[styles.chartContainer, styles.chartContainerOrange]}>
              <CircularProgress percentage={academicPercentage} color="#EA580C" emptyColor="#FFF7ED">
                {academicPercentage > 0 ? (
                  <CountUpPercent value={academicPercentage} style={styles.chartValueOrange} format={String} />
                ) : (
                  <Text style={[styles.chartValueOrange, { fontSize: 20 }]}>N/A</Text>
                )}
                <Text style={styles.chartLabel}>ACADEMIC</Text>
              </CircularProgress>
            </View>
          </View>
        </View>

        {/* Quick actions, all one standard card (owner, 2 Nov 2026). Wallet and Canteen are not
            repeated here: the wallet panel above has its own Wallet and Order food buttons.
            Transport stays out until the backend has a real transport module. */}
        <View style={[styles.quickGrid, isSmallMobile && styles.quickGridRows]}>
          {quickActions.map(action => (
            <QuickActionCard
              key={action.id}
              {...action}
              layout={isSmallMobile ? 'row' : 'tile'}
              basis={isMobile ? '45%' : '30%'}
            />
          ))}
        </View>

        <ContactAdminModal
          visible={contactAdminVisible}
          onClose={() => setContactAdminVisible(false)}
        />

        {/* Class-in-session banner: only rendered while the timetable says a
            teaching period is actually in progress. */}
        {ongoingClass && (
          <View style={styles.liveClassBanner}>
            <View style={styles.liveClassIcon}>
              <Ionicon name="play" size={22} color="#FFF" />
            </View>
            <View style={styles.liveClassInfo}>
              <Text style={styles.liveClassTitle}>Class in session</Text>
              <Text style={styles.liveClassSubtitle}>
                {ongoingClass.subjectName}
                {ongoingClass.endTime ? ` • until ${ongoingClass.endTime}` : ''}
              </Text>
            </View>
            <TouchableOpacity style={styles.watchBtn}
              onPress={() => navigation.navigate('Academics' as any)}>
              <Text style={styles.watchBtnText}>Timetable</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* CXC registration banner: displays both sitting deadlines while registration is open and pupil is in Year 7+. */}
        {isCxcOpen && isStudentEligibleForCxc(selectedStudent) && (
          <CxcBanner
            sittings={sittingsWithStatus}
            subtitle={[selectedStudent?.class, selectedStudent?.name].filter(Boolean).join(' · ')}
            backgroundColor={colors.primary}
            onRegister={() => navigation.navigate('CxcRegistration' as any)}
            wide={!isMobile}
          />
        )}

        {/* Recent Alerts - Show 4-5 initially */}
        <View style={styles.alertsSection}>
          <View style={styles.cardHeader}>
            <Text style={styles.sectionTitle}>Recent alerts</Text>
          </View>

          {data.alerts.length === 0 && recentAlerts.length === 0 ? (
            <Text style={styles.noAlertsText}>No upcoming alerts in the next 7 days.</Text>
          ) : (
            <>
              {recentAlerts.map(alert => (
                <TouchableOpacity
                  key={alert.id}
                  style={styles.alertCard}
                  onPress={() => navigation.navigate('Notifications')}
                >
                  <View style={[styles.alertDot, { backgroundColor: alert.badge.color }]} />
                  <View style={styles.alertInfo}>
                    <Text style={styles.alertTitle} numberOfLines={1}>{alert.name}</Text>
                    <Text style={styles.alertSubtitle} numberOfLines={2}>{alert.details}</Text>
                  </View>
                  <Text style={styles.alertTime}>{formatRelativeTime(alert.createdAt).replace(' ago', '')}</Text>
                </TouchableOpacity>
              ))}

              {data.alerts.slice(0, 5).map((alert, idx) => (
                <TouchableOpacity
                  key={alert.id}
                  style={styles.alertCard}
                  onPress={() => {
                    if (alert.id.startsWith('exam-')) {
                      navigation.navigate('ExamSchedule', {
                        schedule: data.examSchedule || [],
                        initialExamId: alert.examFocus?.examId ?? undefined,
                        focus: alert.examFocus,
                      });
                    } else if (alert.id.startsWith('fee-')) {
                      navigation.navigate('Fees' as any);
                    } else if (alert.id.startsWith('event-')) {
                      navigation.navigate('Communication', { initialTab: 'Events' });
                    }
                  }}
                >
                  <View style={[styles.alertDot, { backgroundColor: alert.color }]} />
                  <View style={styles.alertInfo}>
                    <Text style={styles.alertTitle}>
                      {alert.id.startsWith('exam-') ? 'Upcoming Exam' : alert.title}
                    </Text>
                    <Text style={styles.alertSubtitle}>{alert.subtitle}</Text>
                  </View>
                  <Text style={styles.alertTime}>{formatCountdown(alert.date)}</Text>
                </TouchableOpacity>
              ))}

              {/* See All Button - Show if more than 5 alerts */}
              {data.alerts.length > 5 && (
                <TouchableOpacity
                  style={styles.seeAllButton}
                  onPress={() => navigation.navigate('Notifications' as any)}
                >
                  <Text style={styles.seeAllButtonText}>See all alerts ({data.alerts.length})</Text>
                  <FeatherIcon name="arrow-right" size={16} color={colors.primary} />
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </WebPageShell>
      {needsFee && !feeModalReady && (
        <View style={StyleSheet.absoluteFill}>
          <LoadingSpinner fullScreen />
        </View>
      )}
      <FeeOnboardingModal visible={needsFee && feeModalReady} onSaved={refreshSelectedStudent} />

      {!needsFee && needsProfile && !profileModalReady && (
        <View style={StyleSheet.absoluteFill}>
          <LoadingSpinner fullScreen />
        </View>
      )}
      <ProfileOnboardingModal
        visible={needsProfile && profileModalReady}
        student={selectedStudent}
        onSaved={refreshSelectedStudent}
      />

      <Modal
        visible={showPhotoMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPhotoMenu(false)}
      >
        <View style={styles.photoMenuOverlay}>
          {/* Backdrop and card are SIBLINGS, not parent/child — RN Web's Touchables are
              plain DOM elements underneath, so a press on a nested Touchable would
              bubble up and fire the backdrop's onPress too if the card were nested. */}
          <TouchableOpacity
            style={styles.photoMenuBackdrop}
            activeOpacity={1}
            onPress={() => setShowPhotoMenu(false)}
          />

          <View style={styles.photoMenuCard}>
            <TouchableOpacity
              style={styles.photoMenuCloseBtn}
              onPress={() => setShowPhotoMenu(false)}
            >
              <FeatherIcon name="x" size={18} color={colors.textSecondary} />
            </TouchableOpacity>

            <View style={styles.photoMenuIconBadge}>
              <FeatherIcon name="image" size={22} color={colors.primary} />
            </View>

            <Text style={styles.photoMenuTitle}>Upload Photo</Text>
            <Text style={styles.photoMenuSubtitle}>
              Add a photo using your camera or upload from your device.
            </Text>

            <Div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{ cursor: 'pointer' }}
            >
              <TouchableOpacity
                style={[styles.dropzone, isDragActive && styles.dropzoneActive]}
                onPress={handleUploadFromLibrary}
                activeOpacity={0.8}
              >
                <View style={styles.dropzoneIconCircle}>
                  <FeatherIcon name="upload-cloud" size={26} color={colors.primary} />
                </View>
                <Text style={styles.dropzoneTitle}>Drag &amp; drop a photo here</Text>
                <Text style={styles.dropzoneSubtitle}>PNG or JPG, up to 10MB</Text>
              </TouchableOpacity>
            </Div>

            <View style={styles.photoMenuDividerRow}>
              <View style={styles.photoMenuDividerLine} />
              <Text style={styles.photoMenuDividerText}>OR</Text>
              <View style={styles.photoMenuDividerLine} />
            </View>

            <View style={[styles.photoMenuOptionsRow, isMobile && styles.photoMenuOptionsRowMobile]}>
              <TouchableOpacity style={styles.photoMenuOption} onPress={handleCapturePhoto} activeOpacity={0.8}>
                <View style={styles.photoMenuOptionAccent} />
                <View style={styles.photoMenuOptionIcon}>
                  <FeatherIcon name="camera" size={20} color={colors.primary} />
                </View>
                <View style={styles.photoMenuOptionText}>
                  <Text style={styles.photoMenuOptionTitle}>Use Camera</Text>
                  <Text style={styles.photoMenuOptionSubtitle}>Capture a new photo</Text>
                </View>
              </TouchableOpacity>

              <TouchableOpacity style={styles.photoMenuOption} onPress={handleUploadFromLibrary} activeOpacity={0.8}>
                <View style={styles.photoMenuOptionAccent} />
                <View style={styles.photoMenuOptionIcon}>
                  <FeatherIcon name="upload" size={20} color={colors.primary} />
                </View>
                <View style={styles.photoMenuOptionText}>
                  <Text style={styles.photoMenuOptionTitle}>Upload Photo</Text>
                  <Text style={styles.photoMenuOptionSubtitle}>Select from your device</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <CameraCaptureModal
        visible={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        onCapture={(file) => uploadPhotoFile(file)}
      />

      <Toast
        visible={photoToast.visible}
        message={photoToast.message}
        type={photoToast.type}
        onDismiss={() => setPhotoToast(prev => ({ ...prev, visible: false }))}
      />
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  page: { flex: 1, backgroundColor: colors.background },
  scrollContent: { alignItems: 'center', paddingVertical: 32 },
  column: { width: '100%', maxWidth: 720, paddingHorizontal: 24 },
  pageTitle: { fontSize: 22, fontWeight: '800', color: colors.textPrimary, marginBottom: 20 },

  card: { backgroundColor: colors.card, borderRadius: 24, padding: 24, marginBottom: 20, elevation: 2, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 10, shadowOffset: { width: 0, height: 4 } },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  cardTitle: { fontSize: 17, fontWeight: '800', color: colors.textPrimary },
  // Student details sits in the same 16px gutter as the banner / wallet blocks above it.
  homeSectionHeader: { marginHorizontal: 16, marginTop: 20, marginBottom: 12 },
  homeSectionCard: { marginHorizontal: 16 },
  sectionTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  viewProfileBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primaryLight, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  viewProfileText: { fontSize: 12, color: colors.primary, fontWeight: '700', marginRight: 4 },

  // Photo on the left, the name/grade/ID/year grid filling the rest on the right —
  // one row, not the photo stacked above a full-width grid.
  studentCardRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 24 } as any,
  studentCardRowMobile: { flexDirection: 'column', alignItems: 'center', gap: 16 } as any,
  studentPhotoWrapper: { width: 96, height: 96, position: 'relative', flexShrink: 0 } as any,
  studentPhoto: { width: 96, height: 96, borderRadius: 16, resizeMode: 'cover' as any },
  studentPhotoInitials: {
    width: 96,
    height: 96,
    borderRadius: 16,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  studentPhotoInitialsText: { fontSize: 34, fontWeight: '800', color: colors.primary },
  studentPhotoUploadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
  } as any,
  studentPhotoEditBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.card,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.primary,
  } as any,
  photoMenuOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  // A transparent full-screen layer under the card, so tapping outside it closes it —
  // React Native has no document-level click-outside listener to hook into.
  photoMenuBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
  } as any,
  photoMenuCard: {
    width: '100%',
    // Widened from 440: at that width "Capture a new photo" and "Select from your
    // device" wrapped to two lines inside their option cards, cramping them.
    maxWidth: 560,
    backgroundColor: colors.card,
    borderRadius: 24,
    // A colored top edge, the same premium-dialog cue used by design systems like
    // Material/Fluent to give an otherwise plain white card an identity before the
    // reader even reaches the title.
    borderTopWidth: 4,
    borderTopColor: colors.primary,
    padding: 28,
    paddingTop: 24,
    elevation: 8,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
  } as any,
  photoMenuIconBadge: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 14,
  },
  photoMenuCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  } as any,
  photoMenuTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  photoMenuSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 12,
  },
  dropzone: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colors.border,
    borderRadius: 18,
    backgroundColor: colors.background,
    paddingVertical: 32,
    alignItems: 'center',
    marginBottom: 20,
  } as any,
  dropzoneActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  dropzoneIconCircle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  dropzoneTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  dropzoneSubtitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textMuted,
  },
  photoMenuDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  photoMenuDividerLine: { flex: 1, height: 1, backgroundColor: colors.border },
  photoMenuDividerText: {
    fontSize: 11,
    fontWeight: '800',
    color: colors.textMuted,
    letterSpacing: 1,
  },
  photoMenuOptionsRow: {
    flexDirection: 'row',
    gap: 12,
  } as any,
  photoMenuOptionsRowMobile: {
    flexDirection: 'column',
  } as any,
  photoMenuOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 16,
    padding: 14,
    paddingLeft: 12,
    overflow: 'hidden',
    position: 'relative',
  } as any,
  // A thin colored edge on the leading side, echoing the card's own top accent — the
  // same "this is the primary action" cue on a smaller, per-option scale.
  photoMenuOptionAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: colors.primary,
  } as any,
  photoMenuOptionIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoMenuOptionText: {
    flex: 1,
  },
  photoMenuOptionTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 2,
  },
  photoMenuOptionSubtitle: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textMuted,
  },

  detailsGrid: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', rowGap: 24 } as any,
  detailsGridMobile: { width: '100%', justifyContent: 'center' } as any,
  detailItem: { width: '50%' },
  detailLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '700', letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' },
  detailValue: { fontSize: 16, color: colors.textPrimary, fontWeight: '700' },

  rankBadge: { backgroundColor: '#FFF7ED', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, flexShrink: 0 },
  rankText: { color: '#EA580C', fontSize: 13, fontWeight: '800' },

  performanceCharts: { flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', gap: 16, paddingVertical: 4 },
  // Each metric gets its own tinted, bordered tile instead of floating bare
  // on the card — the standard "stat tile" treatment used elsewhere on the
  // dashboard (fee/attendance action cards), so this section matches them.
  chartContainer: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 12,
    borderRadius: 18,
    borderWidth: 1,
  },
  chartContainerBlue: { backgroundColor: '#F8FAFF', borderColor: '#DBEAFE' },
  chartContainerOrange: { backgroundColor: '#FFFAF7', borderColor: '#FED7AA' },
  chartValueBlue: { fontSize: 24, fontWeight: '800', color: colors.primary },
  chartValueOrange: { fontSize: 24, fontWeight: '800', color: '#C2410C' },
  chartLabel: { fontSize: 10, color: colors.textSecondary, fontWeight: '800', marginTop: 2, letterSpacing: 0.5 },

  cpContainer: { justifyContent: 'center', alignItems: 'center' },
  cpBackground: {},
  cpHalfContainer: { position: 'absolute', overflow: 'hidden' },
  cpHalfCircle: { position: 'absolute', borderBottomColor: 'transparent', borderLeftColor: 'transparent' },

  // Quick actions (QuickActionCard): three across on a wide screen, two on a tablet, rows on a phone.
  quickGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginBottom: 24 },
  quickGridRows: { flexDirection: 'column', gap: 10 },

  liveClassBanner: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary, borderRadius: 20, padding: 20, marginBottom: 24, elevation: 4, shadowColor: colors.primary, shadowOpacity: 0.3, shadowRadius: 12, shadowOffset: { width: 0, height: 6 } },
  liveClassIcon: { width: 50, height: 50, borderRadius: 25, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center', marginRight: 16 },
  liveClassInfo: { flex: 1 },
  liveClassTitle: { color: '#FFF', fontSize: 15, fontWeight: '800', marginBottom: 4 },
  liveClassSubtitle: { color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: '500' },
  watchBtn: { backgroundColor: '#FFF', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 20 },
  watchBtnText: { color: '#2563EB', fontSize: 14, fontWeight: '800' },


  alertsSection: { paddingBottom: 20 },
  seeAllText: { fontSize: 14, color: colors.primary, fontWeight: '700' },
  alertCard: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: colors.card, padding: 18, borderRadius: 20, marginBottom: 12, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.03, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  alertDot: { width: 10, height: 10, borderRadius: 5, marginTop: 6, marginRight: 14 },
  alertInfo: { flex: 1 },
  alertTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  alertSubtitle: { fontSize: 13, color: colors.textSecondary, fontWeight: '500' },
  alertTime: { fontSize: 13, color: colors.textMuted, fontWeight: '600', marginLeft: 12 },
  noAlertsText: { fontSize: 14, color: colors.textMuted, fontStyle: 'italic', paddingVertical: 8 },
  seeAllButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryLight, padding: 16, borderRadius: 20, marginTop: 12, gap: 10 },
  seeAllButtonText: { fontSize: 14, color: colors.primary, fontWeight: '700' },
});
